import { isValidPhoneDisplay } from '#modules/ui/PhoneField';

import {
  OTHER_CHOICE_VALUE,
  type RepeatingListRow,
  type RepeatingListRowState,
} from '../types/assessmentQuestion';

import { dateErrorMessage, numberErrorMessage, type Translate } from './assessmentErrorMessages';
import { getQuestionFlags, type QuestionFlags } from './questionFlags';
import { cellErrorMessage, normalizedCellValue } from './repeatingListCell';
import type { ActiveQuestion } from './resolveQuestionChoices';
import { validateAssessmentDate } from './validateAssessmentDate';
import { validateAssessmentNumber } from './validateAssessmentNumber';

/**
 * One question's pending (not yet committed) input, as the screen holds it.
 * Every field is present regardless of question type — a caller reads its own
 * per-question state out of whatever shape it keeps and hands the whole thing
 * over, rather than each question type needing its own call signature.
 */
export type QuestionPendingState = {
  selected: string[];
  dateValue: string;
  textValue: string;
  otherInput: string;
  rows: RepeatingListRowState[];
};

/**
 * The error-state writes a caller must apply for an evaluation to be visible
 * on screen. An ABSENT field means "leave that error exactly as it is"; a
 * field set to `null` / `false` means "clear it". Returning the writes instead
 * of performing them is what keeps `evaluateQuestionAnswer` pure: the grouped
 * (`singleScreen`) Continue can evaluate every question on the page, then
 * apply each one's patch, and a unit test can assert on the patch without a
 * React tree.
 */
export type QuestionErrorPatch = {
  dateError?: string | null;
  numberError?: string | null;
  phoneError?: string | null;
  showRequiredError?: boolean;
  /** Rows re-validated cell by cell — carries each cell's own message. */
  rows?: RepeatingListRowState[];
};

/** What one question commits into the answers map, by question type. */
export type QuestionAnswerValue = string | string[] | RepeatingListRow[];

/**
 * Outcome of checking whether ONE question's pending state is ready to submit
 * — shared by the single-question Continue and, on a `singleScreen` node, the
 * grouped Continue that checks every question at once. `errors` is always
 * present (possibly empty) and must be applied either way: a passing check
 * also CLEARS the error it just re-validated.
 */
export type EvaluateResult =
  | { ok: true; value: QuestionAnswerValue; otherValue?: string; errors: QuestionErrorPatch }
  | { ok: false; errors: QuestionErrorPatch };

/**
 * Pure date check: the error copy to show (or `null` when the answer is
 * valid), plus the normalized ISO `YYYY-MM-DD` value to commit. Used both by
 * the live blur check and by `evaluateQuestionAnswer` below, so the two read
 * bounds off the same call.
 */
export function checkDateAnswer(
  question: ActiveQuestion,
  value: string,
  t: Translate
): { error: string | null; normalizedDate: string | null } {
  const validation = validateAssessmentDate(value, {
    min: question.minDate,
    max: question.maxDate,
  });
  return validation.isValid
    ? { error: null, normalizedDate: validation.normalizedDate }
    : { error: dateErrorMessage(validation, t), normalizedDate: null };
}

/** Same idea as `checkDateAnswer`, for a standalone numeric free-text question. */
export function checkNumberAnswer(
  question: ActiveQuestion,
  value: string,
  t: Translate
): string | null {
  const validation = validateAssessmentNumber(value, {
    min: question.minNumber,
    max: question.maxNumber,
  });
  return validation.isValid ? null : numberErrorMessage(validation, t);
}

/**
 * Same idea as `checkNumberAnswer`, for the masked `tel` free-text question
 * (doctor phone / fax). `isValidPhoneDisplay` treats '' as valid — irrelevant
 * here, since every caller only reaches this after the blank-vs-required split
 * has already handled an empty value.
 */
export function checkPhoneAnswer(value: string, t: Translate): string | null {
  return isValidPhoneDisplay(value) ? null : t('Enter a valid phone number as (xxx) xxx-xxxx.');
}

function evaluateDateAnswer(
  question: ActiveQuestion,
  state: QuestionPendingState,
  flags: QuestionFlags,
  t: Translate
): EvaluateResult {
  // An optional date left blank is a skip; anything typed still validates.
  if (flags.isOptional && state.dateValue.trim() === '') {
    return { ok: true, value: '', errors: {} };
  }
  const { error, normalizedDate } = checkDateAnswer(question, state.dateValue, t);
  if (error !== null) return { ok: false, errors: { dateError: error } };
  // `normalizedDate` is non-null whenever the check passed; the guard just
  // keeps this total rather than asserting.
  if (!normalizedDate) return { ok: false, errors: { dateError: null } };
  return { ok: true, value: normalizedDate, errors: { dateError: null } };
}

function evaluateFreeTextAnswer(
  question: ActiveQuestion,
  state: QuestionPendingState,
  flags: QuestionFlags,
  t: Translate
): EvaluateResult {
  const trimmed = state.textValue.trim();
  if (trimmed === '') {
    // Same skip-vs-block split as choice questions: an optional blank answer
    // commits '' silently, a required one surfaces the error.
    if (flags.isOptional) return { ok: true, value: '', errors: {} };
    return { ok: false, errors: { showRequiredError: true } };
  }
  const errors: QuestionErrorPatch = {};
  // A non-blank numeric answer must actually be a number (and within any
  // authored min/max) before it commits.
  if (flags.isNumericFreeText) {
    errors.numberError = checkNumberAnswer(question, state.textValue, t);
    if (errors.numberError !== null) return { ok: false, errors };
  }
  // A non-blank tel answer must be a COMPLETE `(xxx) xxx-xxxx` mask before it
  // commits — otherwise Continue would submit whatever partial digits the
  // member had typed so far as their doctor's phone number.
  if (flags.isTelFreeText) {
    errors.phoneError = checkPhoneAnswer(state.textValue, t);
    if (errors.phoneError !== null) return { ok: false, errors };
  }
  return { ok: true, value: trimmed, errors };
}

function evaluateRepeatingListAnswer(
  question: ActiveQuestion,
  state: QuestionPendingState,
  flags: QuestionFlags,
  t: Translate
): EvaluateResult {
  const columns = question.columns ?? [];
  const currentRows = state.rows;
  // Re-validate every cell against its column's cellType before doing
  // anything else. This only touches `errors`, never `values` — an invalid
  // cell keeps whatever the member typed and every OTHER cell stays exactly as
  // it was, so one bad date/number cell blocks Continue without blocking the
  // rest of the list from being edited.
  //
  // A required list also gets a per-ROW completeness check here (Alen, Sep 3):
  // a row the member has STARTED — any one of its cells is non-blank — must
  // have every cell filled, not just the one they happened to type into. A
  // row nobody has touched yet (the trailing "+ Add another" blank, or a
  // prefilled row whose keys `toInitialRows` all dropped as invalid, leaving
  // it blank) stays exempt, so it's dropped as usual below rather than
  // blocking Continue on its own.
  let hasCellErrors = false;
  const isRequired = !flags.isOptional;
  const validatedRows = currentRows.map((row) => {
    const rowStarted = columns.some((column) => (row.values[column.name] ?? '').trim() !== '');
    const errors: Record<string, string | undefined> = { ...row.errors };
    for (const column of columns) {
      const message = cellErrorMessage(column, row.values[column.name] ?? '', t, {
        required: isRequired && rowStarted,
      });
      errors[column.name] = message;
      if (message) hasCellErrors = true;
    }
    return { ...row, errors };
  });
  if (hasCellErrors) {
    // `showRequiredError` rides along with the per-cell messages: otherwise a
    // member who presses Continue on a repeating list long enough that the
    // offending cell is off-screen sees nothing happen. Reuses the same
    // top-level banner the "no rows filled" required case below relies on.
    return { ok: false, errors: { rows: validatedRows, showRequiredError: true } };
  }
  // A row "counts" if at least one of its cells has non-whitespace content; a
  // fully-blank row is dropped rather than submitted as an all-empty object. A
  // counted cell commits `normalizedCellValue` (ISO for a 'date' column)
  // rather than the raw trimmed display string — see that function.
  const columnsByName = new Map(columns.map((column) => [column.name, column]));
  const filledRows = currentRows.reduce<RepeatingListRow[]>((acc, row) => {
    const trimmedValues: RepeatingListRow = {};
    let hasValue = false;
    for (const [columnName, value] of Object.entries(row.values)) {
      const trimmedCell = value.trim();
      if (trimmedCell !== '') hasValue = true;
      const column = columnsByName.get(columnName);
      trimmedValues[columnName] = column ? normalizedCellValue(column, trimmedCell) : trimmedCell;
    }
    if (hasValue) acc.push(trimmedValues);
    return acc;
  }, []);
  if (filledRows.length === 0) {
    // Same skip-vs-block split as every other question type. A repeatingList
    // with no valid columns joins the optional side even when required — see
    // the ticket's "open-ended items" framing: there would be no field on
    // screen for the member to act on the error.
    if (flags.isOptional || columns.length === 0) {
      return { ok: true, value: [], errors: {} };
    }
    return { ok: false, errors: { showRequiredError: true } };
  }
  return { ok: true, value: filledRows, errors: {} };
}

function evaluateChoiceAnswer(
  question: ActiveQuestion,
  state: QuestionPendingState,
  flags: QuestionFlags
): EvaluateResult {
  const selected = state.selected;
  if (selected.length === 0) {
    // Skipping an optional question still commits the key (an empty array for
    // multi-select, '' for single) rather than omitting it — the
    // dynamic-assessment server registers the element name as an
    // allowed_signal_key and rejects an empty answers map.
    if (flags.isOptional) {
      return { ok: true, value: flags.isMultiSelect ? [] : '', errors: {} };
    }
    return { ok: false, errors: { showRequiredError: true } };
  }
  // Multi-select submits the full set as an array; single-select submits the
  // one chosen value as a string. Inline Other text rides a second key.
  if (flags.isMultiSelect) {
    if (flags.hasOtherItem && selected.includes(OTHER_CHOICE_VALUE)) {
      const trimmedOther = state.otherInput.trim();
      if (question.isOtherRequired === true && trimmedOther === '') {
        return { ok: false, errors: { showRequiredError: true } };
      }
      return {
        ok: true,
        value: selected,
        errors: {},
        ...(trimmedOther ? { otherValue: trimmedOther } : {}),
      };
    }
    return { ok: true, value: selected, errors: {} };
  }
  return { ok: true, value: selected[0] ?? '', errors: {} };
}

/**
 * Checks whether `question`'s pending `state` is ready to submit, returning
 * both the value to commit and the error writes the caller must apply.
 *
 * Pure and question-type-driven: the same call serves the single-question
 * Continue (one question) and a `singleScreen` node's grouped Continue (every
 * question on the page), so the two can never disagree about what counts as a
 * valid answer for a given question type.
 */
export function evaluateQuestionAnswer(
  question: ActiveQuestion,
  state: QuestionPendingState,
  t: Translate
): EvaluateResult {
  const flags = getQuestionFlags(question);
  if (flags.isDate) return evaluateDateAnswer(question, state, flags, t);
  if (flags.isFreeText) return evaluateFreeTextAnswer(question, state, flags, t);
  if (flags.isRepeatingList) return evaluateRepeatingListAnswer(question, state, flags, t);
  return evaluateChoiceAnswer(question, state, flags);
}
